import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import {
  listEvents,
  rsvp,
  type EventRealm,
  type EventSummary,
  type RsvpStatus,
} from "@/api/events";
import { EventFinderPanel } from "./EventFinderPanel";

/**
 * Generic events lab (realm=community). For worship-realm events,
 * the WorshipEventsLab passes realm='worship' to the same backend.
 */
export default function EventsLab({ realm = "community" as EventRealm }: { realm?: EventRealm }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["events", realm],
    queryFn: () =>
      listEvents({
        realm,
        limit: 30,
        sort: "event_date",
        order: "asc",
      }),
  });

  async function handleRsvp(eventId: string, status: RsvpStatus) {
    await rsvp(eventId, status);
    qc.invalidateQueries({ queryKey: ["events", realm] });
  }

  const isWorship = realm === "worship";

  return (
    <div className="space-y-6">
      <PageHeader
        title={isWorship ? "Worship Events" : "Events"}
        subtitle={
          isWorship
            ? "Sacred-realm events linked to institutions. Diwali pujas, Gurupurab, Eid prayers, etc."
            : "Community events — cultural gatherings, meetups, social events."
        }
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: isWorship ? "Worship Events" : "Events" },
        ]}
      />

      {!isWorship && (
        <div className="flex gap-4 text-sm">
          <Link to="/lab/events/organizer" className="text-primary-600 hover:underline">
            Organizer console →
          </Link>
          <Link to="/lab/tickets" className="text-primary-600 hover:underline">
            My tickets →
          </Link>
        </div>
      )}

      {!isWorship && <EventFinderPanel />}

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !data || data.data.events.length === 0 ? (
        <p className="text-sm text-gray-500">No upcoming events.</p>
      ) : (
        <ul className="space-y-3">
          {data.data.events.map((e) => (
            <EventRow key={e.id} e={e} onRsvp={handleRsvp} />
          ))}
        </ul>
      )}
    </div>
  );
}

function EventRow({
  e,
  onRsvp,
}: {
  e: EventSummary;
  onRsvp: (eventId: string, status: RsvpStatus) => Promise<void>;
}) {
  const date = new Date(e.eventDate).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  return (
    <li className="bg-white border border-gray-200 rounded-xl px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900">{e.title}</span>
            <Badge variant={e.realm === "worship" ? "purple" : "gray"}>
              {e.realm}
            </Badge>
            {e.category && <Badge variant="gray">{e.category.name}</Badge>}
            {e.isFree && <Badge variant="green">free</Badge>}
            {e.isVirtual && <Badge variant="blue">virtual</Badge>}
            {/*
              The practical facts, on the row rather than behind a tap. These
              are the ones a person filters on while scrolling — and showing
              them here is also how we can see at a glance which events the
              seed has actually reached, since every one of them was null on
              every event until they were seeded.
            */}
            {e.languageCode && <Badge variant="gray">{e.languageCode}</Badge>}
            {e.ageLimitMin != null && (
              <Badge variant="yellow">{e.ageLimitMin}+</Badge>
            )}
          </div>
          {(e.dressCode || e.foodInfo) && (
            <div className="text-xs text-gray-600 mt-1 space-y-0.5">
              {e.foodInfo && <div>🍽 {e.foodInfo}</div>}
              {e.dressCode && <div>👕 {e.dressCode}</div>}
            </div>
          )}
          <div className="text-xs text-gray-500 mt-1">
            {date}
            {e.startTime && ` at ${e.startTime}`}
            {" · "}
            {e.city}, {e.state}
            {" · "}
            {e.rsvpCount} going
            {" · "}
            <Link
              to={`/lab/events/${e.id}/thread`}
              className="text-blue-600 hover:underline"
            >
              thread →
            </Link>
            {" · "}
            <Link
              to={`/lab/events/${e.id}/checkout`}
              className="text-blue-600 hover:underline"
            >
              tickets →
            </Link>
          </div>
        </div>
        <div className="flex gap-1">
          {(["going", "interested", "not_going"] as RsvpStatus[]).map((s) => (
            <Button
              key={s}
              size="sm"
              variant={e.userRsvp === s ? "primary" : "ghost"}
              onClick={() => onRsvp(e.id, s)}
            >
              {s === "going"
                ? "Going"
                : s === "interested"
                  ? "Interested"
                  : "No"}
            </Button>
          ))}
        </div>
      </div>
    </li>
  );
}
